/**
 * Client entry point: base styles and the router.
 *
 * Routes:
 *   /                       start page with game tiles
 *   /spiel/:id              format page
 *   /lehrer                 login / dashboard (?start=<gameId> opens the start dialog)
 *   /lehrer/medien          media library
 *   /lehrer/fragen          Fragenbibliothek
 *   /lehrer/generator       Quiz-Generator (U13)
 *   /lehrer/konten          the admin's teacher accounts (U18)
 *   /anleitungen            guides for teachers, public: overview
 *   /anleitungen/:id        one guide (old links /anleitungen#<id> lead there)
 *   /lehrer/sets/neu        new set (?spiel=<gameId>)
 *   /lehrer/sets/:id        edit set
 *   /lehrer/session/:id     session page (PIN, QR, lobby, links)
 *   /steuerung/:id          teacher control during the game (phone)
 *   /tafel/:id?token=       board
 *   /spielen                student join (?pin= from the QR code)
 */
import '@shared/base.css';
import { t } from '@shared/i18n/de';
import { Router } from '@shared/router';
import { renderBoard } from './hub/board';
import { renderJoin } from './hub/join';
import { forestLayout } from './hub/layout';
import { renderGamePage, renderStart } from './hub/start';
import { renderControl } from './hub/teacher/control';
import { renderTeacher } from './hub/teacher/dashboard';
import { renderEditorPage } from './hub/teacher/editor-page';
import { renderMediaPage } from './hub/teacher/media-page';
import { renderSessionPage } from './hub/teacher/session-page';

const mount = document.getElementById('app');
if (mount === null) throw new Error('#app mount point missing in index.html');

/** The guides: long texts and pictures, read now and then, loaded when opened. */
function guide(m: HTMLElement, id: string | null): () => void {
  let cleanup: (() => void) | undefined;
  let gone = false;
  void import('./hub/guide/guide-page').then(({ renderGuidePage }) => {
    if (!gone) cleanup = renderGuidePage(m, id, window.location.hash);
  });
  return () => {
    gone = true;
    cleanup?.();
  };
}

const router = new Router(mount, (m) => {
  forestLayout(
    m,
    `<div class="empty"><h2>${t('notFound.title')}</h2><a class="btn" href="/" data-link>${t('nav.home')}</a></div>`,
  );
  return undefined;
});

router
  .add('/', (m) => {
    renderStart(m);
    return undefined;
  })
  .add('/spiel/:id', (m, { params }) => {
    renderGamePage(m, params.id ?? '');
    return undefined;
  })
  .add('/lehrer', (m, { query }) => renderTeacher(m, query))
  .add('/lehrer/medien', (m) => renderMediaPage(m))
  .add('/anleitungen', (m) => guide(m, null))
  .add('/anleitungen/:id', (m, { params }) => guide(m, params.id ?? ''))
  .add('/lehrer/konten', (m) => {
    // Only for the admin, rarely: loaded when opened.
    let cleanup: (() => void) | undefined;
    let gone = false;
    void import('./hub/teacher/accounts-page').then(({ renderAccountsPage }) => {
      if (!gone) cleanup = renderAccountsPage(m);
    });
    return () => {
      gone = true;
      cleanup?.();
    };
  })
  .add('/lehrer/generator', (m) => {
    // Like the Fragenbibliothek: only for teachers, loaded when opened.
    let cleanup: (() => void) | undefined;
    let gone = false;
    void import('./hub/teacher/generator-page').then(({ renderGeneratorPage }) => {
      if (!gone) cleanup = renderGeneratorPage(m);
    });
    return () => {
      gone = true;
      cleanup?.();
    };
  })
  .add('/lehrer/fragen', (m, { query }) => {
    // The Fragenbibliothek is big and only for teachers: loaded when opened.
    let cleanup: (() => void) | undefined;
    let gone = false;
    void import('./hub/teacher/bank-page').then(({ renderBankPage }) => {
      if (!gone) cleanup = renderBankPage(m, query);
    });
    return () => {
      gone = true;
      cleanup?.();
    };
  })
  .add('/lehrer/sets/neu', (m, { query }) => renderEditorPage(m, null, query))
  .add('/lehrer/sets/:id', (m, { params, query }) => renderEditorPage(m, params.id ?? '', query))
  .add('/lehrer/session/:id', (m, { params }) => renderSessionPage(m, params.id ?? ''))
  .add('/steuerung/:id', (m, { params }) => renderControl(m, params.id ?? ''))
  .add('/tafel/:id', (m, { params, query }) =>
    renderBoard(m, params.id ?? '', query.get('token') ?? ''),
  )
  .add('/spielen', (m, { query }) => renderJoin(m, query));

router.render();
